import {match, Pattern} from 'ts-pattern';

import {type Ref, useLayoutEffect, useRef, useState} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {debounce} from 'shared/web';

type ElementSize = readonly [number | null, number | null];

interface UseAdaptiveElementSizeOptions {
  debounce?: number;
  resetKey?: string | number;
}

interface ElementSizeGeneration {
  resetKey: UseAdaptiveElementSizeOptions['resetKey'];
  value: number;
}

interface ElementSizeMeasurement {
  generation: number;
  size: ElementSize;
}

interface ObservedResizeEntry {
  contentBoxSize?: ResizeObserverSize | readonly ResizeObserverSize[];
  contentRect?: {width: number; height: number};
}

const EMPTY_SIZE: ElementSize = [null, null];

const toElementSize = (width: number, height: number): ElementSize => [width, height];

const isResizeObserverSize = (
  size: ResizeObserverSize | readonly ResizeObserverSize[]
): size is ResizeObserverSize => 'inlineSize' in size;

// Firefox reports contentBoxSize as a single rect rather than an array.
const getContentBoxSize = (size: ResizeObserverSize | readonly ResizeObserverSize[]) =>
  isResizeObserverSize(size) ? size : size[0];

const readEntrySize = (entry: ObservedResizeEntry | undefined): ElementSize =>
  match(entry)
    .with({contentBoxSize: Pattern.nonNullable}, (observed) => {
      const contentBoxSize = getContentBoxSize(observed.contentBoxSize);
      return toElementSize(contentBoxSize.inlineSize, contentBoxSize.blockSize);
    })
    .with({contentRect: Pattern.nonNullable}, (observed) =>
      toElementSize(observed.contentRect.width, observed.contentRect.height)
    )
    .otherwise(() => EMPTY_SIZE);

export function useAdaptiveElementSize<T extends Element = Element>(
  options?: UseAdaptiveElementSizeOptions
): [Ref<T>, number | null, number | null] {
  const ref = useRef<T>(null);
  const [generation, setGeneration] = useState<ElementSizeGeneration>({
    resetKey: options?.resetKey,
    value: 0,
  });
  const currentGeneration =
    generation.resetKey === options?.resetKey
      ? generation
      : {resetKey: options?.resetKey, value: generation.value + 1};
  if (currentGeneration !== generation) {
    setGeneration(currentGeneration);
  }
  const [measurement, setMeasurement] = useState<ElementSizeMeasurement>({
    generation: currentGeneration.value,
    size: EMPTY_SIZE,
  });
  const size = measurement.generation === currentGeneration.value ? measurement.size : EMPTY_SIZE;

  // Subscribes before paint so the first measurement is not a frame behind the committed element.
  useLayoutEffect(() => {
    const observedNode = ref.current;
    let animationFrame: number | null = null;
    let isCurrentGeneration = true;
    const setSize = (nextSize: ElementSize) => {
      if (isCurrentGeneration) {
        setMeasurement({generation: currentGeneration.value, size: nextSize});
      }
    };
    const setSizeDebounced = isNotNil(options?.debounce)
      ? debounce(setSize, options?.debounce ?? 0, true)
      : setSize;

    function callback(entries: ResizeObserverEntry[]) {
      if (isNotNil(animationFrame)) {
        window.cancelAnimationFrame(animationFrame);
      }

      animationFrame = window.requestAnimationFrame(() => {
        setSizeDebounced(readEntrySize(entries[0]));
      });
    }

    const resizeObserver = new ResizeObserver(callback);
    if (isNotNil(observedNode)) {
      resizeObserver.observe(observedNode);
    }
    return () => {
      isCurrentGeneration = false;
      if (isNotNil(animationFrame)) {
        window.cancelAnimationFrame(animationFrame);
      }
      if (isNotNil(observedNode)) {
        resizeObserver.unobserve(observedNode);
      }
    };
  }, [currentGeneration.value, options?.debounce, options?.resetKey]);

  return [ref, size[0], size[1]];
}
